<template>
  <b-modal
    v-model="visible"
    size="lg"
    scrollable
    title="Análises de IA do Participante"
    modal-class="ai-summary-modal"
    @shown="loadAnalyses"
    @hidden="onHidden"
  >
    <template #modal-footer>
      <b-button variant="secondary" @click="visible = false">Fechar</b-button>
    </template>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="text-muted mt-2 mb-0">Carregando análises...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="analyses.length === 0" class="text-center py-5">
      <i class="bx bx-search-alt text-muted" style="font-size: 48px;"></i>
      <p class="text-muted mt-2 mb-0">Nenhuma análise encontrada para este participante.</p>
    </div>

    <!-- Analysis cards -->
    <div v-else class="ai-summary-list">
      <div
        v-for="item in analyses"
        :key="item.id"
        class="ai-summary-card"
        :class="{ 'ai-summary-card--dismissed': item.is_dismissed }"
      >
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <span class="ai-summary-card__doctype">
              {{ item.document_type_detected || 'Documento' }}
            </span>
            <span
              class="badge ml-2"
              :class="getStatusBadgeClass(item)"
            >
              {{ getStatusLabel(item) }}
            </span>
          </div>
          <small class="text-muted">{{ formatDate(item.created_at) }}</small>
        </div>

        <!-- Summary -->
        <p class="ai-summary-card__summary mb-2">{{ item.summary }}</p>

        <!-- Stats -->
        <div class="ai-summary-card__stats mb-2">
          <span class="ai-summary-card__stat">
            <i class="bx bx-check-circle text-success"></i> {{ item.field_count }} campo(s)
          </span>
          <span v-if="item.mismatch_count > 0" class="ai-summary-card__stat">
            <i class="bx bx-x-circle text-danger"></i> {{ item.mismatch_count }} divergência(s)
          </span>
          <span v-if="item.alert_count > 0" class="ai-summary-card__stat">
            <i class="bx bx-error text-warning"></i> {{ item.alert_count }} alerta(s)
          </span>
        </div>

        <!-- Document alerts -->
        <div v-if="item.document_alerts && item.document_alerts.length > 0" class="ai-summary-card__alerts mb-2">
          <div
            v-for="(alert, aIdx) in item.document_alerts"
            :key="'alert-' + aIdx"
            class="ai-summary-card__alert"
            :class="alert.severity === 'critical' ? 'ai-summary-card__alert--critical' : 'ai-summary-card__alert--warning'"
          >
            <i :class="alert.severity === 'critical' ? 'bx bx-error-circle' : 'bx bx-error'"></i>
            {{ alert.message }}
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex gap-2 mt-2">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openDetail(item)"
          >
            <i class="bx bx-show mr-1"></i> Ver detalhes
          </b-button>
          <b-button
            v-if="!item.is_dismissed"
            size="sm"
            variant="outline-warning"
            @click="confirmDismiss(item)"
          >
            <i class="bx bx-hide mr-1"></i> Ignorar análise
          </b-button>
          <b-button
            v-else
            size="sm"
            variant="outline-success"
            @click="confirmRestore(item)"
          >
            <i class="bx bx-revision mr-1"></i> Restaurar análise
          </b-button>
        </div>
      </div>
    </div>

    <!-- Detail modal (reuses existing AnalysisResultModal) -->
    <analysis-result-modal
      v-model="showDetailModal"
      :attachmentId="selectedAttachmentId"
      :participantId="participantId"
      @applied="loadAnalyses(); $emit('dismissed')"
    />
  </b-modal>
</template>

<script>
import Swal from 'sweetalert2'
import { mapActions } from 'vuex'
import AnalysisResultModal from './AnalysisResultModal'

export default {
  name: 'AiAnalysesSummaryModal',
  components: { AnalysisResultModal },
  props: {
    value: Boolean,
    participantId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      loading: false,
      analyses: [],
      showDetailModal: false,
      selectedAttachmentId: null
    }
  },
  computed: {
    visible: {
      get () { return this.value },
      set (v) { this.$emit('input', v) }
    }
  },
  methods: {
    ...mapActions('participant', {
      fetchAnalyses: 'getParticipantAnalyses',
      doDismiss: 'dismissAnalysis',
      doRestore: 'restoreAnalysis'
    }),
    async loadAnalyses () {
      if (!this.participantId) return
      this.loading = true
      try {
        const res = await this.fetchAnalyses(this.participantId)
        this.analyses = res.data || []
      } catch (err) {
        console.error('Erro ao carregar análises:', err)
        this.analyses = []
      } finally {
        this.loading = false
      }
    },
    getStatusBadgeClass (item) {
      if (item.is_dismissed) return 'badge-secondary'
      if (item.overall_status === 'invalid' || item.mismatch_count > 0) return 'badge-danger'
      return 'badge-success'
    },
    getStatusLabel (item) {
      if (item.is_dismissed) return 'Ignorada'
      if (item.overall_status === 'invalid' || item.mismatch_count > 0) return 'Divergências'
      return 'Validado'
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      return this.$moment(dateStr).format('DD/MM/YYYY [às] HH:mm')
    },
    openDetail (item) {
      this.selectedAttachmentId = item.participant_attachment_id
      this.showDetailModal = true
    },
    async confirmDismiss (item) {
      const result = await Swal.fire({
        title: 'Ignorar análise?',
        text: 'Esta análise não será mais considerada no banner de validação. Você poderá restaurá-la a qualquer momento.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f1b44c',
        confirmButtonText: 'Sim, ignorar',
        cancelButtonText: 'Cancelar'
      })
      if (!result.value) return
      try {
        await this.doDismiss(item.id)
        await this.loadAnalyses()
        this.$emit('dismissed')
      } catch (err) {
        Swal.fire('Erro', 'Não foi possível ignorar a análise.', 'error')
      }
    },
    async confirmRestore (item) {
      const result = await Swal.fire({
        title: 'Restaurar análise?',
        text: 'Esta análise voltará a ser considerada no banner de validação.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        confirmButtonText: 'Sim, restaurar',
        cancelButtonText: 'Cancelar'
      })
      if (!result.value) return
      try {
        await this.doRestore(item.id)
        await this.loadAnalyses()
        this.$emit('dismissed')
      } catch (err) {
        Swal.fire('Erro', 'Não foi possível restaurar a análise.', 'error')
      }
    },
    onHidden () {
      this.analyses = []
      this.selectedAttachmentId = null
    }
  }
}
</script>

<style scoped>
.ai-summary-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ai-summary-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  transition: box-shadow 0.15s;
}

.ai-summary-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.ai-summary-card--dismissed {
  opacity: 0.6;
  background: #f8f9fa;
  border-color: #dee2e6;
}

.ai-summary-card__doctype {
  font-weight: 600;
  font-size: 0.9375rem;
  color: #343a40;
}

.ai-summary-card__summary {
  font-size: 0.8125rem;
  color: #6c757d;
  line-height: 1.5;
}

.ai-summary-card__stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.ai-summary-card__stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: #495057;
}

.ai-summary-card__alerts {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #fef9ee;
  border-radius: 6px;
  border: 1px solid #fde68a;
}

.ai-summary-card__alert {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.ai-summary-card__alert i {
  font-size: 14px;
  flex-shrink: 0;
}

.ai-summary-card__alert--critical {
  color: #dc2626;
}

.ai-summary-card__alert--warning {
  color: #d97706;
}

.d-flex.gap-2 {
  gap: 8px;
}
</style>
